/* © NXAPP Limited */
import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { CheckCircle, AlertTriangle } from 'lucide-react'

/**
 * Unsubscribe confirmation page. It deliberately does NOT act on load — it waits
 * for an explicit click. Auto-unsubscribing on mount would let JS-rendering link
 * scanners and prefetchers opt people out who never intended to, which was
 * silently suppressing real contacts.
 */
export default function UnsubscribePage() {
  const { token } = useParams<{ token: string }>()
  const [state, setState] = useState<'idle' | 'loading' | 'done' | 'error'>('idle')
  const [msg, setMsg] = useState('')

  const confirm = () => {
    if (!token) { setState('error'); setMsg('Invalid unsubscribe link.'); return }
    setState('loading')
    fetch('/api/unsubscribe', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
    })
      .then(r => r.json())
      .then((d: { message?: string; error?: string }) => {
        if (d.error) { setState('error'); setMsg(d.error) }
        else { setState('done'); setMsg(d.message ?? 'You have been unsubscribed.') }
      })
      .catch(() => { setState('error'); setMsg('Something went wrong. Please try again.') })
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="max-w-sm w-full card p-8 text-center space-y-4">
        {(state === 'idle' || state === 'loading') && (
          <>
            <h1 className="text-lg font-semibold text-gray-900">Unsubscribe</h1>
            <p className="text-sm text-gray-500">
              Click below to stop receiving these emails. Nothing changes until you confirm.
            </p>
            <button
              onClick={confirm}
              disabled={state === 'loading'}
              className="btn-primary w-full disabled:opacity-50"
            >
              {state === 'loading' ? 'Processing…' : 'Unsubscribe me'}
            </button>
          </>
        )}
        {state === 'done' && (
          <>
            <CheckCircle className="w-12 h-12 text-green-500 mx-auto" />
            <h1 className="text-lg font-semibold text-gray-900">Unsubscribed</h1>
            <p className="text-sm text-gray-500">{msg}</p>
            <p className="text-xs text-gray-400">You will no longer receive these emails.</p>
          </>
        )}
        {state === 'error' && (
          <>
            <AlertTriangle className="w-12 h-12 text-red-400 mx-auto" />
            <h1 className="text-lg font-semibold text-gray-900">Error</h1>
            <p className="text-sm text-gray-500">{msg}</p>
          </>
        )}
      </div>
    </div>
  )
}
